/**
 * Krok 1: Finance, v10
 * Volba: nahrát PDF (auto-extrakce) NEBO zadat ručně.
 * Vždy zobrazí editovatelný formulář pro potvrzení/úpravu.
 */
import { souboryZeZadani } from '../utils/podklady';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { TooltipLabel } from './TooltipLabel';
import { validatePDFFile } from '../api/pdfParser';
import { validateFinanceInputs } from '../utils/inputValidation';
import { posunNaChybu } from '../utils/chyby';
import { authHeaders } from '../lib/apiFetch';
import { maProdejnu, text } from '../utils/slovnik';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const TOOLTIPS = {
  revenue:            'Celkové tržby firmy za posledních 12 měsíců (aktuální rok). Příklad: 25 000 000 Kč.',
  revenue_prev_year:  'Tržby za předchozí (minulý) rok, slouží k výpočtu meziročního růstu (YoY).',
  revenue_2y_ago:     'Tržby před dvěma lety, volitelné, zobrazuje dvouletý trend růstu.',

  opex:               'Operating Expenses, provozní náklady: marketing, personál, nájmy, logistika, software. Nezahrnuje COGS. Zadáváte ROČNÍ částku. Příklad: roční mzdy 2,4 mil. + nájem 360 tis. + reklama 1,2 mil. + software 240 tis. = OPEX 4,2 mil. Kč za rok.',
  // Přímé náklady a zásoby se mezi typy liší podstatou, ne jen slovy,
  // proto se berou ze slovníku podle zvoleného typu podnikání.
  net_income:         'Čistý zisk po zdanění, "Zisk po zdanění" nebo "Net Income" na konci výsledovky.',
  total_assets:       'Celková aktiva z rozvahy, "Aktiva celkem".',
  equity:             'Vlastní kapitál, "Vlastní kapitál" v sekci Pasiva rozvahy.',
  current_liabilities:'Krátkodobé závazky splatné do 1 roku.',
  total_liabilities:  'Celkové závazky (cizí zdroje), všechny dluhy firmy.',
  interest_bearing_debt: 'Úvěry a půjčky celkem, dlouhodobé i krátkodobé, včetně leasingu a půjček od společníků. Bez závazků vůči dodavatelům, ty sem nepatří. O tuhle částku je hodnota pro majitele nižší, protože úvěry přecházejí na kupujícího nebo se splácejí z kupní ceny.',
  cash:               'Peníze v pokladně a na účtech. Hodnotu pro majitele naopak zvyšují. Čerpaný kontokorent zadejte se záporným znaménkem.',
  real_estate_book:   'Budovy a pozemky v zůstatkové (účetní) hodnotě, v rozvaze řádek „Pozemky a stavby“. Bez nich nelze účetní hodnotu nahradit tržní.',
  real_estate_market: 'Za kolik by se budovy a pozemky firmy dnes daly prodat. Stačí kvalifikovaný odhad, ideálně podle znaleckého posudku nebo nabídek v okolí. Ve zprávě je uvedený jako údaj od klienta.',
};

const UNIT_MULTIPLIERS = { auto: 1, czk: 1, thousands: 1000, millions: 1_000_000 };
const UNIT_LABELS      = { auto: 'auto', czk: 'Kč', thousands: 'tis. Kč', millions: 'mil. Kč' };

// Placeholdery pro každou jednotku ručního vstupu
const PLACEHOLDERS = {
  czk:       { revenue: '25 000 000', prev: '22 000 000', cogs: '12 000 000', opex: '8 000 000', inventory: '3 000 000', net_income: '2 200 000', assets: '15 000 000', equity: '9 000 000', cur_liab: '3 000 000', tot_liab: '6 000 000' },
  thousands: { revenue: '25 000',     prev: '22 000',     cogs: '12 000',     opex: '8 000',     inventory: '3 000',     net_income: '2 200',     assets: '15 000',     equity: '9 000',     cur_liab: '3 000',     tot_liab: '6 000'     },
  millions:  { revenue: '25',         prev: '22',         cogs: '12',         opex: '8',         inventory: '3',         net_income: '2',         assets: '15',         equity: '9',         cur_liab: '3',         tot_liab: '6'         },
};

/**
 * Mapuje raw backend PDF response → naše finance pole.
 * rescaleFrom: pokud uživatel mění jednotky DOPO parsování, předáme původní unit_multiplier
 * a nový multiplikátor (reapplyMultiplier) pro přepočet.
 */
/** Spolehlivost čtení česky. Anglické "inferred" klientovi nic neříká. */
function popisSpolehlivosti(klic) {
  return {
    exact: 'čísla přečtena přímo z výkazu',
    parser_ceske_zaverky: 'čísla přečtena přímo z výkazu',
    inferred: 'čísla odvozena z výkazu, zkontrolujte je',
    fallback: 'čtení nejisté, čísla prosím zkontrolujte',
  }[klic] || 'čísla přečtena z výkazu, zkontrolujte je';
}

function mapPdfToFinance(raw, reapplyMultiplier = null) {
  // If user changed unit after parse, undo the backend multiplier and apply new one
  const originalMult = raw.unit_multiplier || 1;
  const factor = reapplyMultiplier !== null
    ? reapplyMultiplier / originalMult   // undo original, apply new
    : 1;                                 // backend already applied correct multiplier

  // s() vrací '' pro 0/null, zabrání false validation pass s nulou z neextrahovaného pole
  const s  = (v) => v ? v * factor : '';
  // si() vrací 0 pro null, zásoby/závazky mohou být legitimně 0
  const si = (v) => v ? v * factor : 0;

  return {
    revenue:             s(raw.year3_revenue) || s(raw.total_revenue_value),
    revenue_prev_year:   s(raw.year2_revenue),
    revenue_2y_ago:      s(raw.year1_revenue),
    cogs:                s(raw.year3_cogs),
    opex:                s(raw.year3_opex),
    inventory:           si(raw.inventory_value),
    net_income:          s(raw.net_profit_value),
    total_assets:        si(raw.total_assets_value),
    equity:              s(raw.equity_value),
    current_liabilities: si(raw.short_term_liabilities_value),
    total_liabilities:   si(raw.external_sources_value),
    interest_bearing_debt: si(raw.interest_bearing_debt_value),
    cash:                s(raw.cash_value),
    showBalanceSheet: !!(raw.total_assets_value || raw.equity_value),
  };
}

// Mapování řádků Výkazu zisku a ztráty (účetní závěrka) → naše pole
function guideRows(typ) {
  return [
  {
    accent: 'var(--color-success)', badge: 'Tržby', badgeBg: 'rgba(47,143,91,0.14)', badgeColor: 'var(--color-success)',
    rows: text('trzby_radek', typ),
    why: text('trzby_proc', typ),
  },
  {
    accent: 'var(--gold)', badge: 'COGS', badgeBg: 'rgba(184,147,45,0.16)', badgeColor: 'var(--gold)',
    rows: text('cogs_radek', typ),
    why: text('cogs_proc', typ),
  },
  {
    accent: 'var(--purple-glow)', badge: 'OPEX', badgeBg: 'var(--purple-mid)', badgeColor: 'var(--star-white)',
    rows: 'A.3. Služby  +  D. Osobní náklady (mzdy)  +  spotřeba energií',
    why: 'Provozní náklady na chod firmy: reklama, doprava, software, nájem, mzdy. Zkrátka všechno kromě nákupu zboží.',
  },
  {
    accent: 'var(--text-dim)', badge: 'nezadávat', badgeBg: 'var(--purple-deep)', badgeColor: 'var(--text-dim)',
    rows: 'E. Úpravy hodnot v provozní oblasti (odpisy)',
    why: 'Odpisy do OPEX NEdávejte. Počítáme EBITDA, tedy zisk PŘED odpisy, díky tomu vyjde hodnota firmy férověji.',
  },
  ];
}

/** Klidná, názorná ukázka, který řádek výkazu opsat a proč. */
function FinanceGuide({ typ }) {
  return (
    <details open style={{ marginBottom: '1rem', background: 'var(--purple-deep)', border: '1px solid rgba(91,59,140,0.18)', borderRadius: 12, padding: '0.85rem 1rem' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 700, color: 'var(--star-white)' }}>
        Ukážeme vám to přímo ve výkazu zisku a ztráty
      </summary>
      <div style={{ marginTop: '0.75rem' }}>
        <div style={{ fontSize: '0.86rem', color: 'var(--text-dim)', lineHeight: 1.55, marginBottom: '0.75rem' }}>
          Takhle vypadají řádky ve výkazu (účetní závěrce) a kam je u nás zapsat. Stačí čísla opsat, nic dalšího řešit nemusíte.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
          {guideRows(typ).map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start', background: 'var(--purple-dark)', border: '1px solid rgba(0,0,0,0.07)', borderLeft: `4px solid ${r.accent}`, borderRadius: 8, padding: '0.6rem 0.8rem' }}>
              <span style={{ flexShrink: 0, fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.03em', background: r.badgeBg, color: r.badgeColor, padding: '0.2rem 0.5rem', borderRadius: 999, marginTop: '0.1rem', minWidth: 64, textAlign: 'center' }}>
                {r.badge}
              </span>
              <div>
                <div style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--star-white)', fontFamily: 'monospace' }}>{r.rows}</div>
                <div style={{ fontSize: '0.83rem', color: 'var(--text-dim)', lineHeight: 1.5, marginTop: '0.15rem' }}>{r.why}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '0.75rem', fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.5 }}>
          Pod čísly se vám rovnou ukáže <strong>EBITDA</strong> (provozní zisk) a marže, ať vidíte, že to sedí.
          U každého pole je navíc ikonka <strong>?</strong> s příkladem.
        </div>
      </div>
    </details>
  );
}

/** Kde výkaz vzít. */
function FinanceSourceHint() {
  return (
    <div style={{ marginBottom: '1.25rem', fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.5, background: 'var(--purple-deep)', border: '1px dashed rgba(0,0,0,0.12)', borderRadius: 10, padding: '0.6rem 0.85rem' }}>
      <strong style={{ color: 'var(--star-white)' }}>Kde výkaz vezmu?</strong> V účetním programu (Pohoda, Money, Fakturoid, iDoklad…),
      od své účetní, nebo zdarma na <strong>justice.cz</strong> → Veřejný rejstřík → vaše firma → Sbírka listin → účetní závěrka.
      Použijte poslední uzavřený rok.
    </div>
  );
}

export function Step1_Finance({ wizardState, setWizardState, onNext, onBack }) {
  // Otázky i nápovědy se řídí typem podnikání. Strojírna nemá kamennou
  // prodejnu a její přímé náklady jsou materiál, ne nakoupené zboží.
  const typ = wizardState.typ_podnikani || '';
  // Nahrání závěrky je hlavní cesta. Ručně zadaná čísla si nikdo neověří
  // a celé ocenění na nich stojí, zatímco údaje ze závěrky si kupující
  // najde ve sbírce listin. Ruční zadání zůstává pro toho, kdo závěrku
  // po ruce nemá.
  // Kdo v úvodním kroku zvolil ruční zadání, začíná rovnou formulářem.
  const [mode, setMode]       = useState(wizardState.zadani?.cesta === 'rucne' ? 'manual' : 'pdf'); // 'pdf' | 'manual'
  const [errors, setErrors]   = useState({});

  // Jednotka ručního vstupu, default tis. Kč (nejběžnější v ČR)
  const savedUnit = wizardState.financeUnit || 'thousands';
  const [manualUnit, setManualUnit] = useState(savedUnit);

  // Přepočet hodnot při změně jednotky, aby 1749 tis. → 1 749 000 Kč → 1.75 mil.
  const NUMERIC_FIELDS = [
    'revenue', 'revenue_prev_year', 'revenue_2y_ago',
    'cogs', 'opex', 'inventory', 'net_income',
    'total_assets', 'equity', 'current_liabilities', 'total_liabilities',
    'interest_bearing_debt', 'cash', 'real_estate_book', 'real_estate_market',
    'owner_salary_actual', 'owner_salary_market',
    'personal_expenses_through_business', 'one_time_costs',
    'physical_store_costs',
  ];
  const handleManualUnitChange = (newUnit) => {
    if (newUnit === manualUnit) return;
    const oldMult = UNIT_MULTIPLIERS[manualUnit] || 1;
    const newMult = UNIT_MULTIPLIERS[newUnit] || 1;
    const ratio = oldMult / newMult;
    const convert = (v) => {
      if (v === '' || v === undefined || v === null) return v;
      const num = Number(v);
      if (isNaN(num) || num === 0) return v;
      return num * ratio;
    };
    setForm(prev => {
      const updated = { ...prev };
      for (const field of NUMERIC_FIELDS) {
        updated[field] = convert(prev[field]);
      }
      return updated;
    });
    setManualUnit(newUnit);
  };

  // Při návratu zpět: wizardState.finance je v plných Kč → vydělíme uloženou jednotkou
  const savedFactor = UNIT_MULTIPLIERS[savedUnit] || 1;
  const fromKc = (v) => (v === '' || v === 0 || v === undefined) ? '' : Number(v) / savedFactor;
  const [form, setForm] = useState({
    ...wizardState.finance,
    revenue:             fromKc(wizardState.finance.revenue),
    revenue_prev_year:   fromKc(wizardState.finance.revenue_prev_year),
    revenue_2y_ago:      fromKc(wizardState.finance.revenue_2y_ago),
    cogs:                fromKc(wizardState.finance.cogs),
    opex:                fromKc(wizardState.finance.opex),
    inventory:           fromKc(wizardState.finance.inventory),
    net_income:          fromKc(wizardState.finance.net_income),
    total_assets:        fromKc(wizardState.finance.total_assets),
    equity:              fromKc(wizardState.finance.equity),
    current_liabilities: fromKc(wizardState.finance.current_liabilities),
    total_liabilities:   fromKc(wizardState.finance.total_liabilities),
    interest_bearing_debt: fromKc(wizardState.finance.interest_bearing_debt),
    cash:                fromKc(wizardState.finance.cash),
    real_estate_book:    fromKc(wizardState.finance.real_estate_book),
    real_estate_market:  fromKc(wizardState.finance.real_estate_market),
    owner_salary_actual: fromKc(wizardState.finance.owner_salary_actual),
    owner_salary_market: fromKc(wizardState.finance.owner_salary_market),
    personal_expenses_through_business: fromKc(wizardState.finance.personal_expenses_through_business),
    one_time_costs:      fromKc(wizardState.finance.one_time_costs),
    physical_store_costs: fromKc(wizardState.finance.physical_store_costs),
  });
  // Závěrky nahrané v úvodním kroku Zadání se převezmou, klient je
  // nenahrává podruhé.
  const [pdfFiles, setPdfFiles]       = useState(() => souboryZeZadani(wizardState.zadani));
  const [parsing, setParsing]         = useState(false);
  const [pdfError, setPdfError]       = useState(null);
  const [pdfConfidence, setPdfConfidence] = useState(null);
  const [dragOver, setDragOver]       = useState(false);
  const [unitOverride, setUnitOverride] = useState('auto'); // 'auto'|'czk'|'thousands'|'millions'
  const [detectedUnit, setDetectedUnit] = useState(null);   // what Claude reported
  const [rawPdfResult, setRawPdfResult] = useState(null);   // last raw API response for re-scaling

  /* ── helpers ── */
  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };
  const n   = v => Number(v) || 0;
  const fmt = v => n(v).toLocaleString('cs-CZ');

  // Jednotka ručního zadávání
  const manualUnitFactor = UNIT_MULTIPLIERS[manualUnit] || 1;
  const manualUnitLabel  = UNIT_LABELS[manualUnit] || 'Kč';
  const ph = PLACEHOLDERS[manualUnit] || PLACEHOLDERS.thousands;

  // EBITDA preview (v zadané jednotce, marže jsou správně bez násobení)
  const grossProfit  = n(form.revenue) - n(form.cogs);
  const ebitda       = grossProfit - n(form.opex);
  // Provozní ztráta, ne prázdný formulář (bez tržeb vychází EBITDA nula).
  const ztrata       = n(form.revenue) > 0 && ebitda <= 0;
  const grossMargin  = n(form.revenue) > 0 ? (grossProfit / n(form.revenue) * 100).toFixed(1) : '-';
  const ebitdaMargin = n(form.revenue) > 0 ? (ebitda / n(form.revenue) * 100).toFixed(1) : '-';
  const yoy          = n(form.revenue_prev_year) > 0
    ? (((n(form.revenue) - n(form.revenue_prev_year)) / n(form.revenue_prev_year)) * 100).toFixed(1)
    : '-';
  const yoy2y        = n(form.revenue_2y_ago) > 0 && n(form.revenue_prev_year) > 0
    ? (((n(form.revenue_prev_year) - n(form.revenue_2y_ago)) / n(form.revenue_2y_ago)) * 100).toFixed(1)
    : null;

  /* ── PDF upload ── */
  const MAX_FILES = 10;

  const addFiles = (incoming) => {
    const newValid = [];
    const errs = [];
    for (const f of incoming) {
      const v = validatePDFFile(f);
      if (v.valid) newValid.push(f);
      else errs.push(`${f.name}: ${v.error}`);
    }
    setPdfFiles(prev => {
      const merged = [...prev, ...newValid];
      const deduped = merged.filter(
        (f, i, arr) => arr.findIndex(x => x.name === f.name && x.size === f.size) === i
      );
      return deduped.slice(0, MAX_FILES);
    });
    if (errs.length) setPdfError(errs.join('; '));
    else setPdfError(null);
  };

  const handleFileSelect = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files || []).filter(f => f.type === 'application/pdf'));
  };

  const removeFile = (idx) => {
    setPdfFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleParsePDF = async () => {
    if (!pdfFiles.length) return;
    setParsing(true);
    setPdfError(null);

    const formData = new FormData();
    pdfFiles.forEach(f => formData.append('files', f));
    formData.append('unit', unitOverride);

    try {
      const res = await fetch(`${API_BASE}/api/parse-pdfs`, { method: 'POST', headers: await authHeaders(), body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const raw = await res.json();
      if (!raw.success) throw new Error(raw.error || 'Extrakce dat selhala');

      setRawPdfResult(raw);
      // Jednotka, ve které byl výkaz vytištěný (parser českých závěrek
      // vrací násobitel, ne název jednotky).
      setDetectedUnit(raw.detected_unit
        || ({ 1000: 'thousands', 1000000: 'millions' }[raw.unit_multiplier] || 'czk'));
      setForm(prev => ({ ...prev, ...mapPdfToFinance(raw) }));
      // Values from API are in full Kč, set unit to czk to prevent double multiplication
      setManualUnit('czk');
      setPdfConfidence(
        raw.files_parsed > 1
          ? `${popisSpolehlivosti(raw.confidence)} · sloučeno ${raw.files_parsed} PDF · jednotky: ${raw.unit_label || 'Kč'}`
          : `${popisSpolehlivosti(raw.confidence)} · jednotky: ${raw.unit_label || 'Kč'}`
      );
      if (raw.skeny_vynechany?.length) {
        setPdfError(`Naskenované soubory jsme vynechali, skeny zatím spolehlivě přečíst neumíme: ${raw.skeny_vynechany.join(', ')}. Čísla z nich prosím doplňte ručně.`);
      }
      setMode('manual');
    } catch (err) {
      setPdfError(`Chyba: ${err.message}`);
    } finally {
      setParsing(false);
    }
  };

  // Závěrky ze zadání se přečtou hned při otevření kroku. Rozbor je už
  // přečetl, klient je nemá spouštět podruhé; čísla jen zkontroluje.
  const automatickaExtrakce = useRef(false);
  useEffect(() => {
    if (automatickaExtrakce.current) return;
    automatickaExtrakce.current = true;
    if (souboryZeZadani(wizardState.zadani).length && !n(form.revenue)) handleParsePDF();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Přepočet hodnot při manuální změně jednotek po extrakci z PDF
  const handleUnitRescale = (newUnit) => {
    setUnitOverride(newUnit);
    if (rawPdfResult) {
      const newMultiplier = UNIT_MULTIPLIERS[newUnit] || 1;
      setForm(prev => ({ ...prev, ...mapPdfToFinance(rawPdfResult, newMultiplier) }));
      setDetectedUnit(newUnit);
      // Hodnoty ve formuláři jsou po přepočtu už v korunách. Kdyby se
      // jednotka nastavila i pro uložení, násobily by se podruhé a tržby
      // 61,9 mil. by se změnily na 61,9 mld. (plán v2, D3).
      setManualUnit('czk');
    }
  };

  /* ── Validace ── */
  const validate = () => {
    const e = {};
    if (!n(form.revenue))           e.revenue           = 'Povinné pole';
    if (!n(form.revenue_prev_year)) e.revenue_prev_year = 'Povinné pole';
    if (n(form.cogs) === 0 && !form.cogs) e.cogs        = 'Zadej 0 pokud COGS = 0';
    if (n(form.opex) === 0 && !form.opex) e.opex        = 'Zadej 0 pokud OPEX = 0';
    // Záporná EBITDA, varování, ale NEblokujeme (audit je stále možný)
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validate()) { posunNaChybu(); return; }
    // Vždy ukládáme do wizardState v plných Kč (× faktor)
    const f = manualUnitFactor;
    const toKc = (v) => n(v) * f;
    setWizardState(prev => ({
      ...prev,
      financeUnit: manualUnit,
      finance: {
        ...form,
        revenue:             toKc(form.revenue),
        revenue_prev_year:   toKc(form.revenue_prev_year),
        revenue_2y_ago:      toKc(form.revenue_2y_ago),
        cogs:                toKc(form.cogs),
        opex:                toKc(form.opex),
        inventory:           toKc(form.inventory),
        net_income:          toKc(form.net_income),
        total_assets:        toKc(form.total_assets),
        equity:              toKc(form.equity),
        current_liabilities: toKc(form.current_liabilities),
        total_liabilities:   toKc(form.total_liabilities),
        // Prázdné pole zůstává prázdné. Nula by tvrdila, že firma úvěry
        // nemá, a to není totéž jako že je neznáme.
        interest_bearing_debt: form.interest_bearing_debt === '' ? '' : toKc(form.interest_bearing_debt),
        cash:                form.cash === '' ? '' : toKc(form.cash),
        real_estate_book:    form.real_estate_book === '' || form.real_estate_book == null ? '' : toKc(form.real_estate_book),
        real_estate_market:  form.real_estate_market === '' || form.real_estate_market == null ? '' : toKc(form.real_estate_market),
        // Při záporném provozním zisku drží hodnotu majetek, rozvaha je
        // pak podklad pro ocenění, ne doplněk.
        showBalanceSheet:    form.showBalanceSheet || ztrata,
        // Kamenná pobočka
        has_physical_stores: form.has_physical_stores,
        physical_store_count: n(form.physical_store_count) || null,
        physical_store_revenue_pct: n(form.physical_store_revenue_pct) || null,
        physical_store_costs: toKc(form.physical_store_costs),
        // Normalizace EBITDA, přepočet na Kč
        normalize_ebitda:    form.normalize_ebitda,
        owner_salary_actual: toKc(form.owner_salary_actual),
        owner_salary_market: toKc(form.owner_salary_market),
        personal_expenses_through_business: toKc(form.personal_expenses_through_business),
        one_time_costs:      toKc(form.one_time_costs),
        owner_operates:      form.owner_operates,
      },
    }));
    onNext();
  };

  /* ── Render ── */
  return (
    <div className="step-container">
      <h2>Finanční data</h2>
      <p className="step-description">
        Potřebujeme jen pár čísel z jednoho papíru, z <strong>Výkazu zisku a ztráty</strong> (součást účetní
        závěrky). Nemusíte ničemu rozumět, níže vám ukážeme, který řádek opsat a proč. Když si nebudete vědět
        rady, klikněte vpravo dole na <strong>Poradce</strong> nebo se zeptejte své účetní.
      </p>

      <FinanceGuide typ={typ} />
      <FinanceSourceHint />

      {/* PDF sekce */}
      {mode === 'pdf' && (
        <div className="edit-form" style={{ marginBottom: '1.5rem' }}>
          <p style={{ fontSize: '0.9rem', color: 'var(--star-white)', marginBottom: '1rem' }}>
            Nahrajte účetní závěrku v PDF, rozvahu i výsledovku. Čísla z ní
            přečteme sami a vy je jen potvrdíte. Můžete nahrát až <strong>10
            souborů najednou</strong>, třeba každý rok zvlášť nebo rozvahu
            a výsledovku odděleně.
          </p>

          <input
            type="file"
            accept=".pdf"
            multiple
            id="pdf-input"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            disabled={parsing}
          />
          <label
            htmlFor="pdf-input"
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              display: 'block',
              padding: '2.5rem 2rem',
              border: `2px dashed ${dragOver ? 'var(--purple-glow)' : 'var(--purple-mid)'}`,
              borderRadius: 12,
              textAlign: 'center',
              cursor: parsing ? 'not-allowed' : 'pointer',
              background: dragOver ? 'var(--purple-deep)' : 'var(--purple-deep)',
              marginBottom: '1rem',
              transition: 'all 0.15s',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>
              {dragOver ? 'Pusť soubory sem...' : 'Klikni pro výběr nebo přetáhni PDF sem'}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginTop: '0.3rem' }}>
              max 10 souborů · max 10 MB každý · pouze PDF
            </div>
          </label>

          {pdfFiles.length > 0 && (
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {pdfFiles.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: 'var(--purple-deep)', borderRadius: 8, padding: '0.45rem 0.75rem',
                  fontSize: '0.88rem', color: 'var(--star-white)',
                }}>
                  <span>{f.name} <span style={{ color: 'var(--text-dim)' }}>({(f.size / 1024).toFixed(0)} kB)</span></span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    disabled={parsing}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}
                  >×</button>
                </div>
              ))}
              {pdfFiles.length < MAX_FILES && (
                <label htmlFor="pdf-input" style={{ fontSize: '0.82rem', color: 'var(--purple-glow)', cursor: 'pointer', textDecoration: 'underline', marginTop: '0.2rem' }}>
                  + Přidat další soubory ({pdfFiles.length}/{MAX_FILES})
                </label>
              )}
            </div>
          )}

          {/* Jednotky dokumentu */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--star-white)', marginBottom: '0.4rem' }}>
              Jednotky v dokumentu
              <span style={{ fontWeight: 400, color: 'var(--text-dim)', marginLeft: '0.4rem' }}>
                (detekujeme automaticky, uprav jen pokud víš, co dokument používá)
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {[
                { id: 'auto',     label: 'Automaticky', desc: 'automatická detekce' },
                { id: 'czk',      label: 'Kč',          desc: 'celá čísla' },
                { id: 'thousands',label: 'tis. Kč',     desc: 'tisíce, nejčastější' },
                { id: 'millions', label: 'mil. Kč',     desc: 'miliony' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setUnitOverride(opt.id)}
                  style={{
                    padding: '0.35rem 0.85rem',
                    border: `2px solid ${unitOverride === opt.id ? 'var(--purple-glow)' : 'var(--purple-mid)'}`,
                    borderRadius: 8,
                    background: unitOverride === opt.id ? 'var(--purple-deep)' : 'white',
                    color: unitOverride === opt.id ? 'var(--purple-glow)' : 'var(--star-white)',
                    fontWeight: unitOverride === opt.id ? 700 : 400,
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                  title={opt.desc}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pdfError && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{pdfError}</div>}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              className="btn-primary"
              onClick={handleParsePDF}
              disabled={!pdfFiles.length || parsing}
            >
              {parsing
                ? 'Čtu data z PDF…'
                : pdfFiles.length
                  ? `Extrahovat data z PDF (${pdfFiles.length} ${pdfFiles.length === 1 ? 'soubor' : pdfFiles.length < 5 ? 'soubory' : 'souborů'})`
                  : 'Extrahovat data z PDF'}
            </button>
            <button className="btn-secondary" onClick={() => setMode('manual')}>
              Zadat ručně
            </button>
          </div>
        </div>
      )}

      {/* Ruční formulář, vždy přítomný v manual módu, nebo po PDF parsování */}
      {mode === 'manual' && (
        <div className="edit-form">

          {/* Cesta zpět k nahrání. Bez ní se klient, který jednou klikl
              na ruční zadání, k závěrce už nedostane. */}
          {!pdfConfidence && (
            <div style={{ marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
              Máte účetní závěrku v PDF?{' '}
              <button
                type="button"
                onClick={() => setMode('pdf')}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  font: 'inherit', color: 'var(--gold)', textDecoration: 'underline',
                }}
              >
                Nahrajte ji a čísla vyplníme za vás
              </button>
            </div>
          )}

          {/* Přepínač jednotek, pouze v ručním zadávání (ne po PDF) */}
          {!pdfConfidence && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--star-white)' }}>Zadávám hodnoty v:</span>
              {[
                { id: 'czk',       label: 'Kč',       hint: 'celá čísla' },
                { id: 'thousands', label: 'tis. Kč',  hint: 'nejčastější v ČR' },
                { id: 'millions',  label: 'mil. Kč',  hint: 'velké firmy' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleManualUnitChange(opt.id)}
                  title={opt.hint}
                  style={{
                    padding: '0.3rem 0.85rem',
                    border: `2px solid ${manualUnit === opt.id ? 'var(--purple-glow)' : 'var(--purple-mid)'}`,
                    borderRadius: 8,
                    background: manualUnit === opt.id ? 'var(--purple-deep)' : 'white',
                    color: manualUnit === opt.id ? 'var(--purple-glow)' : 'var(--star-white)',
                    fontWeight: manualUnit === opt.id ? 700 : 400,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  {opt.label}{opt.id === 'thousands' && manualUnit !== 'thousands' ? '' : ''}
                </button>
              ))}
              {manualUnit === 'thousands' && (
                <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>← doporučeno</span>
              )}
            </div>
          )}

          {pdfError && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{pdfError}</div>}
          {pdfConfidence && (
            <div style={{ background: 'var(--purple-deep)', border: '1px solid var(--purple-mid)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.88rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>
                Data extrahována z PDF · {pdfConfidence}
              </div>
              <div style={{ color: 'var(--star-white)', marginBottom: '0.5rem' }}>
                Zkontrolujte čísla níže. Pokud vypadají o 1 000× jinak, než čekáte, změňte jednotky:
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {[
                  { id: 'czk',      label: 'Kč (celá čísla)' },
                  { id: 'thousands',label: 'tis. Kč' },
                  { id: 'millions', label: 'mil. Kč' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleUnitRescale(opt.id)}
                    style={{
                      padding: '0.3rem 0.7rem',
                      border: `2px solid ${detectedUnit === opt.id ? 'var(--purple-glow)' : 'var(--purple-mid)'}`,
                      borderRadius: 6,
                      background: detectedUnit === opt.id ? 'var(--purple-glow)' : 'white',
                      color: detectedUnit === opt.id ? 'white' : 'var(--purple-glow)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '0.82rem',
                    }}
                  >
                    {opt.label}{detectedUnit === opt.id ? ' ✓' : ''}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Tržby, 3 roky ─────────────────────────────────────────────── */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--star-white)', marginBottom: '0.6rem' }}>
              Vývoj tržeb za 3 roky * ({manualUnitLabel})
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr', gap: '0.5rem', alignItems: 'start' }}>
              {/* Před 2 lety */}
              <div className="form-group" style={{ margin: 0 }}>
                <TooltipLabel label="Před 2 lety (volitelné)" tooltip={TOOLTIPS.revenue_2y_ago} />
                <input type="number" value={form.revenue_2y_ago} onChange={e => update('revenue_2y_ago', e.target.value)} placeholder={ph.prev} min="0" />
              </div>
              {/* Šipka + růst 2y→1y */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '1.7rem', gap: '0.1rem', minWidth: '2.2rem' }}>
                {yoy2y !== null && (
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: Number(yoy2y) >= 0 ? 'var(--color-success)' : 'var(--color-danger)', whiteSpace: 'nowrap' }}>
                    {Number(yoy2y) >= 0 ? '+' : ''}{yoy2y} %
                  </span>
                )}
                <span style={{ color: 'var(--text-dim)' }}>→</span>
              </div>
              {/* Minulý rok */}
              <div className="form-group" style={{ margin: 0 }}>
                <TooltipLabel label={`Minulý rok * (${manualUnitLabel})`} tooltip={TOOLTIPS.revenue_prev_year} />
                <input type="number" value={form.revenue_prev_year} onChange={e => update('revenue_prev_year', e.target.value)} placeholder={ph.prev} min="0" />
                {errors.revenue_prev_year && <small className="chyba-pole">{errors.revenue_prev_year}</small>}
              </div>
              {/* Šipka + YoY */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '1.7rem', gap: '0.1rem', minWidth: '2.2rem' }}>
                {n(form.revenue_prev_year) > 0 && n(form.revenue) > 0 && (
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: Number(yoy) >= 0 ? 'var(--color-success)' : 'var(--color-danger)', whiteSpace: 'nowrap' }}>
                    {Number(yoy) >= 0 ? '+' : ''}{yoy} %
                  </span>
                )}
                <span style={{ color: 'var(--text-dim)' }}>→</span>
              </div>
              {/* Aktuální rok */}
              <div className="form-group" style={{ margin: 0 }}>
                <TooltipLabel label={`Aktuální rok * (${manualUnitLabel})`} tooltip={TOOLTIPS.revenue} />
                <input type="number" value={form.revenue} onChange={e => update('revenue', e.target.value)} placeholder={ph.revenue} min="0" />
                {errors.revenue && <small className="chyba-pole">{errors.revenue}</small>}
              </div>
            </div>
          </div>

          {/* ── COGS, OPEX, Zásoby (aktuální rok) ─────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <TooltipLabel label={`COGS, aktuální rok * (${manualUnitLabel})`} tooltip={text('cogs', typ)} />
              <input type="number" value={form.cogs} onChange={e => update('cogs', e.target.value)} placeholder={ph.cogs} min="0" />
              <small>Nákupní cena prodaného zboží za posledních 12 měsíců</small>
              {errors.cogs && <small className="chyba-pole">{errors.cogs}</small>}
            </div>
            <div className="form-group">
              <TooltipLabel label={`OPEX, aktuální rok * (${manualUnitLabel})`} tooltip={TOOLTIPS.opex} />
              <input type="number" value={form.opex} onChange={e => update('opex', e.target.value)} placeholder={ph.opex} min="0" />
              <small>Marketing, mzdy, nájmy, software… za posledních 12 měsíců</small>
              {errors.opex && <small className="chyba-pole">{errors.opex}</small>}
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <TooltipLabel label={`Zásoby na skladě (${manualUnitLabel})`} tooltip={text('zasoby', typ)} />
              <input type="number" value={form.inventory} onChange={e => update('inventory', e.target.value)} placeholder={ph.inventory} min="0" />
              <small>Reálná hodnota zásob, kterou by kupující našel při due diligence</small>
            </div>
          </div>

          {/* EBITDA live preview */}
          {n(form.revenue) > 0 && (
            <div style={{ background: ebitda > 0 ? 'var(--purple-deep)' : 'rgba(192,69,63,0.08)', border: `1px solid ${ebitda > 0 ? 'var(--purple-mid)' : 'rgba(192,69,63,0.35)'}`, borderRadius: 8, padding: '0.85rem', marginBottom: '0.75rem', fontSize: '0.88rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                <div><div style={{ color: 'var(--text-dim)' }}>Gross Profit</div><div style={{ fontWeight: 700 }}>{fmt(grossProfit)} {manualUnitLabel}</div></div>
                <div><div style={{ color: 'var(--text-dim)' }}>EBITDA</div><div style={{ fontWeight: 700, color: ebitda > 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>{fmt(ebitda)} {manualUnitLabel}</div></div>
                <div><div style={{ color: 'var(--text-dim)' }}>Gross marže</div><div style={{ fontWeight: 700 }}>{grossMargin} %</div></div>
                <div><div style={{ color: 'var(--text-dim)' }}>EBITDA marže</div><div style={{ fontWeight: 700 }}>{ebitdaMargin} %</div></div>
                {n(form.revenue_prev_year) > 0 && (
                  <div style={{ gridColumn: '1 / -1', fontSize: '0.83rem', color: 'var(--star-white)' }}>
                    YoY růst: <strong style={{ color: Number(yoy) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>{yoy} %</strong>
                  </div>
                )}
              </div>
              {ebitda <= 0 && (
                <div style={{ marginTop: '0.75rem', padding: '0.75rem 0.85rem', background: 'rgba(184,147,45,0.10)', border: '1px solid rgba(184,147,45,0.35)', borderRadius: 6, color: 'var(--gold)', fontSize: '0.84rem', lineHeight: 1.55 }}>
                  <strong>EBITDA je záporná ({fmt(ebitda)} {manualUnitLabel})</strong>

                  <div style={{ marginTop: '0.45rem', color: 'var(--gold)' }}>
                    EBITDA je provozní zisk firmy. Když je záporná, nedá se firma ocenit násobkem
                    zisku. Metodu proto vybereme podle majetku a vývoje tržeb.
                  </div>

                  <div style={{ marginTop: '0.4rem', color: 'var(--star-white)', fontSize: '0.82rem' }}>
                    EBITDA = Tržby ({fmt(n(form.revenue))}) − COGS ({fmt(n(form.cogs))}) − OPEX ({fmt(n(form.opex))}) = <strong style={{ color: 'var(--color-danger)' }}>{fmt(ebitda)}</strong>
                  </div>

                  <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.7rem', background: 'var(--purple-dark)', border: '1px solid var(--purple-mid)', borderRadius: 6, color: 'var(--star-white)' }}>
                    <strong>Ocenění pokračuje jinou metodou.</strong> U firmy s významným majetkem, typicky
                    vlastní budovou, se oceňuje čistý majetek s nemovitostmi v tržní hodnotě. Vyplňte proto
                    níže rozvahové údaje včetně budov a pozemků. Nejpřesnější je nahrát účetní závěrku
                    v kroku Zadání.
                  </div>

                  <div style={{ marginTop: '0.4rem', color: 'var(--text-dim)', fontSize: '0.8rem' }}>
                    Zkontrolujte také, zda COGS a OPEX nejsou zadané chybně (prohozené jednotky, zdvojené položky).
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cross-validace vstupů (sanity, bez porovnání s průměrem segmentu) */}
          {n(form.revenue) > 0 && wizardState.segmentBenchmarks && (() => {
            const financeWarnings = validateFinanceInputs(form, wizardState.segmentBenchmarks);
            if (!financeWarnings.length) return null;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.5rem' }}>
                {financeWarnings.map((w, i) => (
                  <div key={i} style={{
                    padding: '0.45rem 0.75rem', marginTop: '0.15rem',
                    background: w.severity === 'high' ? 'rgba(220, 38, 38, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                    border: `1px solid ${w.severity === 'high' ? 'rgba(220, 38, 38, 0.25)' : 'rgba(245, 158, 11, 0.25)'}`,
                    borderRadius: 8, fontSize: '0.82rem',
                    color: w.severity === 'high' ? 'var(--color-danger)' : 'var(--gold)',
                  }}>
                    {w.message}
                  </div>
                ))}
              </div>
            );
          })()}

          {errors._ebitda && <div className="error-message">{errors._ebitda}</div>}

          {/* Kamenná pobočka. Ptáme se jen u prodeje zboží koncovým
              zákazníkům. Strojírna ani poradenská firma prodejnu nemá
              a otázka na ni podrývá důvěru ve zbytek formuláře. */}
          {maProdejnu(typ) && (
          <div style={{ marginTop: '1rem', borderTop: '1px solid var(--purple-mid)', paddingTop: '1rem' }}>
            <button
              type="button"
              onClick={() => update('has_physical_stores', !form.has_physical_stores)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--purple-glow)', fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              {form.has_physical_stores ? '▼' : '▶'} Kamenná pobočka
              <span style={{ fontWeight: 400, color: 'var(--text-dim)', fontSize: '0.82rem' }}>
                (volitelné, máte-li prodejnu, upravíme výpočet)
              </span>
            </button>

            {form.has_physical_stores && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ background: 'var(--purple-deep)', border: '1px solid var(--purple-mid)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.84rem', color: 'var(--star-white)', lineHeight: 1.55 }}>
                  Prodej online a prodej na prodejně mají jinou nákladovou strukturu i jiné zákazníky. Když je oddělíme, vyjde ocenění přesněji.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Počet prodejen</label>
                    <input type="number" value={form.physical_store_count} onChange={e => update('physical_store_count', e.target.value)} placeholder="1" min="1" />
                  </div>
                  <div className="form-group">
                    <label>% tržeb z prodejen</label>
                    <input type="number" value={form.physical_store_revenue_pct} onChange={e => update('physical_store_revenue_pct', e.target.value)} placeholder="30" min="0" max="100" />
                  </div>
                  <div className="form-group">
                    <label>Náklady prodejen ({manualUnitLabel}/rok)</label>
                    <input type="number" value={form.physical_store_costs} onChange={e => update('physical_store_costs', e.target.value)} placeholder="500" min="0" />
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Normalizace zisku se nyní řeší v samostatném Kroku 3 (Očištění zisku) */}

          {/* Rozvaha, volitelná sekce */}
          <div style={{ marginTop: '1rem', borderTop: '1px solid var(--purple-mid)', paddingTop: '1rem' }}>
            <button
              type="button"
              onClick={() => update('showBalanceSheet', !form.showBalanceSheet)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--purple-glow)', fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              {form.showBalanceSheet ? '▼' : '▶'} Rozvahové údaje
              <span style={{ fontWeight: 400, color: 'var(--text-dim)', fontSize: '0.82rem' }}>
                (volitelné, odemkne Finanční zdraví: ROA, ROE, ROS…)
              </span>
            </button>

            {(form.showBalanceSheet || ztrata) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                <div className="form-group">
                  <TooltipLabel label={`Čistý zisk, Net Income (${manualUnitLabel})`} tooltip={TOOLTIPS.net_income} />
                  <input type="number" value={form.net_income} onChange={e => update('net_income', e.target.value)} placeholder={ph.net_income} />
                </div>
                <div className="form-group">
                  <TooltipLabel label={`Celková aktiva (${manualUnitLabel})`} tooltip={TOOLTIPS.total_assets} />
                  <input type="number" value={form.total_assets} onChange={e => update('total_assets', e.target.value)} placeholder={ph.assets} min="0" />
                </div>
                <div className="form-group">
                  <TooltipLabel label={`Vlastní kapitál (${manualUnitLabel})`} tooltip={TOOLTIPS.equity} />
                  <input type="number" value={form.equity} onChange={e => update('equity', e.target.value)} placeholder={ph.equity} />
                </div>
                <div className="form-group">
                  <TooltipLabel label={`Krátkodobé závazky (${manualUnitLabel})`} tooltip={TOOLTIPS.current_liabilities} />
                  <input type="number" value={form.current_liabilities} onChange={e => update('current_liabilities', e.target.value)} placeholder={ph.cur_liab} min="0" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <TooltipLabel label={`Celkové závazky (${manualUnitLabel})`} tooltip={TOOLTIPS.total_liabilities} />
                  <input type="number" value={form.total_liabilities} onChange={e => update('total_liabilities', e.target.value)} placeholder={ph.tot_liab} min="0" />
                </div>

                {/* Úvěry a hotovost stojí zvlášť od ostatních závazků. Jen
                    o ně se liší hodnota provozu od toho, co majitel při
                    prodeji dostane. Bez nich zpráva uvede jen hodnotu
                    provozu a řekne, že most chybí. */}
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <div style={{
                    fontSize: '0.68rem', letterSpacing: '0.16em',
                    textTransform: 'uppercase', color: 'var(--text-dim)',
                    fontWeight: 600, margin: '0.4rem 0 0.1rem',
                  }}>
                    Pro výpočet hodnoty pro majitele
                  </div>
                  <small style={{ color: 'var(--text-dim)', display: 'block', marginBottom: '0.5rem' }}>
                    Úvěry hodnotu snižují, hotovost ji zvyšuje. Bez těchto
                    dvou údajů uvedeme jen hodnotu provozu.
                  </small>
                </div>
                <div className="form-group">
                  <TooltipLabel label={`Úvěry a půjčky (${manualUnitLabel})`} tooltip={TOOLTIPS.interest_bearing_debt} />
                  <input type="number" value={form.interest_bearing_debt} onChange={e => update('interest_bearing_debt', e.target.value)} placeholder="0" min="0" />
                </div>
                <div className="form-group">
                  <TooltipLabel label={`Peníze v pokladně a na účtech (${manualUnitLabel})`} tooltip={TOOLTIPS.cash} />
                  <input type="number" value={form.cash} onChange={e => update('cash', e.target.value)} placeholder="0" />
                </div>

                {/* Nemovitosti. U firmy, jejíž hodnotu drží budova, se oceňuje
                    majetkem a účetní hodnota se nahrazuje tržní. */}
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <div style={{
                    fontSize: '0.68rem', letterSpacing: '0.16em',
                    textTransform: 'uppercase', color: 'var(--text-dim)',
                    fontWeight: 600, margin: '0.4rem 0 0.1rem',
                  }}>
                    Budovy a pozemky
                  </div>
                  <small style={{ color: 'var(--text-dim)', display: 'block', marginBottom: '0.5rem' }}>
                    Jen pokud je firma vlastní. Když hodnotu drží nemovitost, oceníme firmu majetkem
                    s nemovitostí v tržní hodnotě.
                  </small>
                </div>
                <div className="form-group">
                  <TooltipLabel label={`Účetní hodnota (${manualUnitLabel})`} tooltip={TOOLTIPS.real_estate_book} />
                  <input type="number" value={form.real_estate_book ?? ''} onChange={e => update('real_estate_book', e.target.value)} placeholder="0" min="0" />
                </div>
                <div className="form-group">
                  <TooltipLabel label={`Odhad tržní hodnoty (${manualUnitLabel})`} tooltip={TOOLTIPS.real_estate_market} />
                  <input type="number" value={form.real_estate_market ?? ''} onChange={e => update('real_estate_market', e.target.value)} placeholder="0" min="0" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {Object.keys(errors).length > 0 && mode === 'pdf' && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          Přejděte na <strong>Zadat ručně</strong> a vyplňte povinná pole označená *.
        </div>
      )}
      <div className="button-group">
        {onBack && <button onClick={onBack} className="btn-secondary">Zpět</button>}
        <button onClick={handleNext} className="btn-primary">Pokračovat</button>
      </div>
    </div>
  );
}
